import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ASSETS_LIABILITIES_SUMMARY_ITEMS,
  ASSETS_LIABILITIES_TABLES,
  ASSETS_SUMMARY_KEYS,
  AssetsLiabilitiesColumn,
  AssetsLiabilitiesTable,
  LIABILITY_SUMMARY_KEYS,
  OTHER_OUTFLOW_SUMMARY_KEYS,
} from '../../../../constants/assets-and-liabilities-constant';

@Component({
    selector: 'app-assets-and-liabilities-details-en-v2',
    templateUrl: './assets-and-liabilities-details-en-v2.component.html',
    styleUrls: [
        './assets-and-liabilities-details-en-v2.component.css',
        '../../../shared/return-view-text-fields.css',
    ],
    standalone: false
})
export class AssetsAndLiabilitiesDetailsEnV2Component implements OnInit, OnChanges {
  @Input() returnData: any;
  @Input() printActive: boolean = false;

  readonly sections = [
    'Assets',
    'Liabilities (Outside Business)',
    'Other Outflow',
    'Net Wealth of Previous Income Year',
  ];
  readonly summaryItems = ASSETS_LIABILITIES_SUMMARY_ITEMS;
  summaryData: Record<string, number> = {};

  taxableIncome = 0;
  taxExemptedIncome = 0;
  otherReceipt: Array<{ description: string; relatedIN?: string; value: any }> = [];
  hasSourceFundData = false;

  private readonly particularsByKey: Record<string, any> = {};

  constructor() { }

  getTablesForSection(section: string): AssetsLiabilitiesTable[] {
    return ASSETS_LIABILITIES_TABLES.filter((table) => table.section === section);
  }

  hasSectionData(section: string): boolean {
    return this.getTablesForSection(section).some((table) => this.shouldRenderTable(table));
  }

  shouldRenderTable(table: AssetsLiabilitiesTable): boolean {
    if (table.excludeFromView) {
      return false;
    }
    if (table.key === 'banks_cards_and_other_electronic_cash') {
      return this.getRows(table).length > 0 || this.getNotesAndCurrenciesAmount() > 0;
    }
    return this.getRows(table).length > 0;
  }

  getRows(table: AssetsLiabilitiesTable): any[] {
    return this.getParticularRows(table.key).filter((row: any) => this.hasMeaningfulRow(row, table));
  }

  hasData(): boolean {
    return this.hasSourceFundData
      || ASSETS_LIABILITIES_TABLES.some((table) => this.shouldRenderTable(table));
  }

  getValue(row: any, column: AssetsLiabilitiesColumn): any {
    if (!row || !column.key) {
      return '';
    }
    if (column.key === '__particulars') {
      return 'Cash in Hand Amount at the End of Income Year';
    }
    if (column.unitKey) {
      return this.formatValueWithUnit(row, column);
    }
    const value = row[column.key];
    const labelKey = value === null || value === undefined ? '' : String(value);
    return column.valueLabels && column.valueLabels[labelKey]
      ? column.valueLabels[labelKey]
      : value;
  }

  getTextFieldAmount(rows: any[]): number {
    return rows.reduce((sum, row) => sum + this.toNumber(row ? row.value : 0), 0);
  }

  private formatValueWithUnit(row: any, column: AssetsLiabilitiesColumn): string {
    const rawValue = row[column.key];
    const rawUnit = column.unitKey ? row[column.unitKey] : null;
    const valuePart = rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();
    const unitPart = rawUnit === null || rawUnit === undefined ? '' : String(rawUnit).trim();
    if (valuePart && unitPart) {
      return `${valuePart} ${unitPart}`;
    }
    return valuePart || unitPart;
  }

  getColumnTotal(rows: any[], column: AssetsLiabilitiesColumn): number {
    return rows.reduce((sum, row) => sum + this.toNumber(row ? row[column.key] : 0), 0);
  }

  getAmountColumnTotal(
    table: AssetsLiabilitiesTable,
    rows: any[],
    column: AssetsLiabilitiesColumn
  ): number {
    const rowsTotal = this.getColumnTotal(rows, column);
    if (table.key === 'banks_cards_and_other_electronic_cash' && column.key === 'value') {
      const notesAmount = this.getNotesAndCurrenciesAmount();
      return notesAmount > 0 ? notesAmount : rowsTotal;
    }
    return rowsTotal;
  }

  shouldShowSubtotalAmount(table: AssetsLiabilitiesTable, column: AssetsLiabilitiesColumn): boolean {
    if (!column.isAmount) {
      return false;
    }
    if (table.multiSubTotal) {
      return column.key === 'startYrAmount' || column.key === 'endYrAmount';
    }
    return column.key === 'value' || column.key === 'endYrAmount';
  }

  getSummaryValue(key: string): number {
    const value = this.summaryData[key];
    return value === null || value === undefined ? 0 : value;
  }

  private prepareParticulars(): void {
    Object.keys(this.particularsByKey).forEach((key) => delete this.particularsByKey[key]);

    this.getAllParticulars().forEach((particular: any) => {
      const key = this.getParticularKey(particular);
      if (key) {
        this.particularsByKey[key] = particular;
      }
    });
  }

  private calculateSummary(): void {
    let totalGrossWealth = this.getTotalForKeys(ASSETS_SUMMARY_KEYS);
    if (this.getNotesAndCurrenciesAmount() === 0) {
      totalGrossWealth += this.getTotalForKeys(['banks_cards_and_other_electronic_cash']);
    }

    const totalLiabilities = this.getTotalForKeys(LIABILITY_SUMMARY_KEYS);
    const previousIncomeYearNetWealth = this.getTotalForKeys(['previous_income_year_net_wealth']);
    const otherFundOutflow = this.getTotalForKeys(OTHER_OUTFLOW_SUMMARY_KEYS);
    const sourcesOfFund = this.getSourcesOfFundTotal();
    const totalNetWealth = totalGrossWealth - totalLiabilities;
    const changeInNetWealth = totalNetWealth - previousIncomeYearNetWealth;
    const totalFundOutflow = changeInNetWealth + otherFundOutflow;

    this.summaryData = {
      totalGrossWealth: this.roundAmount(totalGrossWealth),
      totalLiabilities: this.roundAmount(totalLiabilities),
      totalNetWealth: this.roundAmount(totalNetWealth),
      previousIncomeYearNetWealth: this.roundAmount(previousIncomeYearNetWealth),
      changeInNetWealth: this.roundAmount(changeInNetWealth),
      otherFundOutflow: this.roundAmount(otherFundOutflow),
      totalFundOutflow: this.roundAmount(totalFundOutflow),
      sourcesOfFund: this.roundAmount(sourcesOfFund),
      difference: this.roundAmount(sourcesOfFund - totalFundOutflow),
    };
  }

  private getSourcesOfFundTotal(): number {
    const taxPayment = this.returnData?.taxPaymentData || {};
    const incomeShown = this.getIncomeShown(taxPayment);
    const exemptedShown = this.getExemptedShown(taxPayment);
    const otherReceipts = this.getKeyTotal('other_receipts');
    const lossNotSetOff = this.roundAmount(taxPayment.set_Off_Total_Loss_Not_Set_Off);
    const alreadyIncludesLoss = this.otherReceipt.some((item) =>
      item?.description === 'Loss Not Set Off' || item?.relatedIN === 'Section 70'
    );

    return incomeShown + exemptedShown + otherReceipts + (alreadyIncludesLoss ? 0 : lossNotSetOff);
  }

  private getIncomeShown(taxPayment: any): number {
    const income = taxPayment?.set_Off_Head_Total_Income_After_Set_Off
      ? this.roundAmount(taxPayment.set_Off_Head_Total_Income_After_Set_Off)
      : 0;
    const deemed = this.toNumber(taxPayment?.poti_DEEMED_Amt);
    const result = income - deemed;
    return result > 0 ? result : 0;
  }

  private getExemptedShown(taxPayment: any): number {
    const income = taxPayment?.set_Off_Head_Total_Income_After_Set_Off
      ? this.roundAmount(taxPayment.set_Off_Head_Total_Income_After_Set_Off)
      : 0;
    const deemed = this.toNumber(taxPayment?.poti_DEEMED_Amt);
    const exempted = taxPayment?.set_Off_Head_Tax_Exempted_After_Set_Off
      ? this.roundAmount(taxPayment.set_Off_Head_Tax_Exempted_After_Set_Off)
      : 0;
    return income < deemed ? exempted - (deemed - income) : exempted;
  }

  private getTotalForKeys(keys: string[]): number {
    return keys.reduce((total, key) => total + this.getKeyTotal(key), 0);
  }

  private getKeyTotal(key: string): number {
    const particular = this.particularsByKey[key];
    const helperValue = this.toNumber(particular?.amountHelperOfAL?.value);
    if (helperValue) {
      return helperValue;
    }

    const table = ASSETS_LIABILITIES_TABLES.find((item) => item.key === key);
    const ignoredFields = new Set(
      (table?.columns || [])
        .filter((column) => column.ignoreCalculation)
        .map((column) => column.key)
    );
    const rows = table ? this.getRows(table) : this.getParticularRows(key);
    return rows.reduce((rowTotal, row) => rowTotal + this.getRowEndAmount(row, ignoredFields), 0);
  }

  private getParticularRows(key: string): any[] {
    const particular = this.particularsByKey[key];
    if (!particular) {
      return [];
    }

    if (Array.isArray(particular.detailsOfAL)) {
      return particular.detailsOfAL;
    }
    if (Array.isArray(particular.alDetailsDtos)) {
      return particular.alDetailsDtos;
    }
    return [];
  }

  private getNotesAndCurrenciesAmount(): number {
    return this.getTotalForKeys(['notes_and_currencies']);
  }

  private getAllParticulars(): any[] {
    const lists = [
      this.returnData?.assetData?.alParticularsDtos,
      this.returnData?.alParticularsDtos,
      this.returnData?.headSummery?.assetsAndLiabilities,
      this.returnData?.detailReturnView?.alParticularsDtos,
    ];
    const byKey = new Map<string, any>();

    lists.forEach((list) => {
      if (!Array.isArray(list)) {
        return;
      }
      list.forEach((particular) => {
        const key = this.getParticularKey(particular);
        if (!key) {
          return;
        }
        const existing = byKey.get(key);
        if (!existing) {
          byKey.set(key, particular);
          return;
        }
        byKey.set(key, {
          ...existing,
          ...particular,
          amountHelperOfAL: particular.amountHelperOfAL || existing.amountHelperOfAL,
          detailsOfAL: this.pickRicherRows(existing.detailsOfAL, particular.detailsOfAL),
          alDetailsDtos: this.pickRicherRows(existing.alDetailsDtos, particular.alDetailsDtos),
        });
      });
    });

    return Array.from(byKey.values());
  }

  private pickRicherRows(current: any, incoming: any): any {
    const currentLength = Array.isArray(current) ? current.length : 0;
    const incomingLength = Array.isArray(incoming) ? incoming.length : 0;
    if (incomingLength > currentLength) {
      return incoming;
    }
    return current || incoming;
  }

  private getParticularKey(particular: any): string {
    const secondKey = particular?.secondSubTypeKey;
    if (secondKey !== null && secondKey !== undefined && String(secondKey).trim() !== '') {
      return secondKey;
    }
    return particular?.firstSubTypeKey || '';
  }

  private roundAmount(value: number): number {
    const rounded = Math.round(value);
    return isNaN(rounded) ? 0 : rounded;
  }

  private getRowEndAmount(row: any, ignoredFields: Set<string>): number {
    if (!row) {
      return 0;
    }
    const valueAmount = this.toNumber(row.value);
    if (valueAmount && !ignoredFields.has('value')) {
      return valueAmount;
    }
    const endYearAmount = this.toNumber(row.endYrAmount);
    if (endYearAmount && !ignoredFields.has('endYrAmount')) {
      return endYearAmount;
    }
    return 0;
  }

  private hasMeaningfulRow(row: any, table: AssetsLiabilitiesTable): boolean {
    if (!row) {
      return false;
    }
    return table.columns.some((column) => {
      if (!column.key || column.key.startsWith('__')) {
        return false;
      }
      const value = row[column.key];
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return true;
      }
      if (column.unitKey) {
        const unit = row[column.unitKey];
        return unit !== null && unit !== undefined && String(unit).trim() !== '';
      }
      return false;
    });
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const numberValue = Number(String(value).replace(/,/g, ''));
    return isNaN(numberValue) ? 0 : numberValue;
  }

  private prepareSourceFund(): void {
    this.otherReceipt = [];
    this.applyNonCashBenefitView();
    const taxPayment = this.returnData?.taxPaymentData || {};
    this.taxableIncome = this.returnData?.taxPaymentData?.poti_Total_Income_Amt_with_deemed || 0;
    this.taxExemptedIncome = this.getExemptedShown(taxPayment);
    this.collectOtherReceipts();
    this.hasSourceFundData =
      this.taxableIncome > 0
      || this.taxExemptedIncome !== 0
      || this.otherReceipt[0]?.description !== '-';
  }

  private applyNonCashBenefitView(): void {
    let income = this.returnData?.taxPaymentData?.poti_Total_Income_Amt;
    let exempted = this.returnData?.taxPaymentData?.poti_IFEMP_EXEMPTED_Amt;
    const deed = this.returnData?.taxPaymentData?.poti_DEEMED_Amt ?? 0;

    if (income > deed) {
      income -= deed;
    } else {
      exempted -= (deed - income);
      income = 0;
    }

    if (this.returnData?.taxPaymentData) {
      this.returnData.taxPaymentData.poti_Total_Income_Amt_with_deemed = income;
      this.returnData.taxPaymentData.poti_IFEMP_EXEMPTED_Amt_with_deemed = exempted;
    }
  }

  private collectOtherReceipts(): void {
    this.getAllParticulars().forEach((item: any) => {
      if (item?.typeKey === 'sources_of_fund' && item?.firstSubTypeKey === 'other_receipts') {
        const rows = Array.isArray(item.detailsOfAL)
          ? item.detailsOfAL
          : Array.isArray(item.alDetailsDtos)
            ? item.alDetailsDtos
            : [];
        this.otherReceipt = this.otherReceipt.concat(rows);
      }
    });

    if (this.otherReceipt.length === 0) {
      this.otherReceipt.push({ description: '-', value: 0 });
    }
  }

  private refreshView(): void {
    this.prepareParticulars();
    this.prepareSourceFund();
    this.calculateSummary();
  }

  ngOnInit(): void {
    this.refreshView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['returnData']) {
      this.refreshView();
    }
  }

}
